/**
 * The empty-state system (CAE-1540) — the three flavours the design draws, from
 * `.admin-empty*` in the handoff's `setup-rail.css` and `.rl-empty` /
 * `.m-emptymkts` in its `modals.css`.
 *
 * Three components rather than one with a flag, because the design draws the
 * distinction on purpose and §6 turns on it: **nothing-exists offers creation,
 * filters-matched-nothing offers escape.** Collapsing them would let a screen
 * put a "Create" pill over a filtered list, which is the one thing the design
 * is trying to prevent.
 *
 *   AdminEmpty         a screen's primary collection has never been filled
 *   AdminEmptyInline   filters returned zero rows
 *   AdminEmptySection  a hole inside a section that has content around it
 *
 * Every string is a prop. The design's copy is per screen — the Contests list
 * alone has four filtered headlines — so it lives in the screen tickets, not
 * here.
 *
 * Sizes worth knowing, because the ticket describing this got them wrong: the
 * "56×56" and "36×36" the spec talks about are the **icon**; the tile around it
 * is 88 and 56 respectively. See `prototype/setup-rail.css`.
 */
import { useEffect, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import type { Theme } from '@mui/material/styles';
import type { ColorTokens } from '../../theme';
import { VISUALLY_HIDDEN } from './visually-hidden';

/**
 * The filtered empty's single action. No glyph: the design draws it as
 * `.m-link`, plain accent text, and a narrower type here is what stops a caller
 * passing an icon that would be silently dropped.
 */
export interface AdminEmptyLinkAction {
  label: string;
  onClick: () => void;
}

export interface AdminEmptyAction extends AdminEmptyLinkAction {
  /**
   * The leading glyph — `PlusIcon` on every create CTA, `RefreshIcon` on the
   * feed's retry. Decoration on an already-labelled button, so MUI's
   * `startIcon` hides it from the accessible name for us.
   */
  icon?: ReactNode;
  /**
   * Offer the action but refuse it — for a collection that is empty *and*
   * closed to writes. Edit › Prizes on a settled contest is the first of
   * these: the screen still has to say what a prize tier is for, and the CTA
   * beside that sentence must not open an editor the API will reject
   * (CAE-1554). The empty state's whole job is to be the one place the action
   * lives, so dropping the button instead would leave the state mute.
   */
  disabled?: boolean;
}

/**
 * The announcement — a live region that is in the DOM *before* it has anything
 * to say.
 *
 * Wrapping the whole empty state in `role="status"` looks right and does
 * nothing. A live region only speaks content that changes *inside* it, and when
 * a list unmounts and an empty state mounts, the region and its text arrive in
 * the same commit — so assistive tech treats it as ordinary inserted content
 * and stays silent. That is exactly the case this exists for: a filter returns
 * zero rows and the rows swap for an empty state mid-session.
 *
 * So the region mounts empty and is filled a tick later, which is a change it
 * can see. It carries the headline alone: the visible block stays in the
 * reading order for anyone navigating to it, and repeating the subtitle and
 * both CTAs into the announcement would talk over them.
 */
const EmptyAnnouncement = ({ children }: { children: string }) => {
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => setAnnouncement(children), [children]);

  return (
    <Box role="status" sx={VISUALLY_HIDDEN}>
      {announcement}
    </Box>
  );
};

/**
 * The tile an icon sits in has to size the icon, not merely contain it — and
 * MUI's `SvgIcon` defaults to a `fontSizeMedium` class pinning itself to
 * 1.5rem, which beats an inherited `font-size`. So every tile here sets its own
 * size and then forces its child to take it.
 *
 * On the tile rather than in `empty-icons.tsx`, because these components accept
 * whatever icon a screen passes. `AdminEmptySection` in particular is drawn
 * with `line-icons.tsx`'s glyphs in the handoff, and those carry MUI's default
 * — a tile that only worked with our own icons would silently render them at
 * 24px instead of the design's 13.
 */
const inheritIconSize = { '& svg': { fontSize: 'inherit' } } as const;

/**
 * `.admin-empty-art` — the rounded tile the line art sits in, at the two sizes
 * the design draws. The icon inside is two-thirds of the tile in both, which is
 * why one number drives both here.
 */
const ART_TILE = {
  lg: { tile: '88px', radius: '24px', icon: '56px' },
  sm: { tile: '56px', radius: '16px', icon: '36px' },
} as const;

const EmptyArt = ({ icon, size }: { icon: ReactNode; size: keyof typeof ART_TILE }) => {
  const shape = ART_TILE[size];

  return (
    <Box
      // Decorative: the art repeats the title and says nothing the title does
      // not, so it stays out of the reading.
      aria-hidden
      sx={(theme: Theme) => ({
        width: shape.tile,
        height: shape.tile,
        borderRadius: shape.radius,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4px',
        backgroundColor: theme.tokens.componentColor.emptyArtBg,
        color: theme.tokens.color.ink3,
        fontSize: shape.icon,
        ...inheritIconSize,
      })}
    >
      {icon}
    </Box>
  );
};

/** The two-line explanation under either centred flavour's title. */
const EmptySubtitle = ({ children }: { children: string }) => (
  <Box
    component="p"
    sx={(theme: Theme) => ({
      margin: 0,
      maxWidth: '440px',
      fontSize: '13.5px',
      lineHeight: 1.5,
      textWrap: 'pretty',
      color: theme.tokens.color.ink3,
    })}
  >
    {children}
  </Box>
);

/**
 * `.m-link` — the filtered empty's single action. A link in weight, a button in
 * behaviour: it clears state on the page it is already on, so it is a `button`
 * and not an anchor.
 */
const EmptyTextLink = ({ label, onClick }: AdminEmptyLinkAction) => (
  <ButtonBase
    onClick={onClick}
    sx={(theme: Theme) => ({
      fontFamily: theme.tokens.font.sans,
      fontSize: '12.5px',
      fontWeight: 500,
      letterSpacing: '-0.005em',
      color: theme.tokens.color.accent,
      '&:hover': { textDecoration: 'underline' },
      // `ButtonBase` sets `outline: 0` and leaves the focus ring to whichever
      // component wraps it. This is the only control in a filtered empty state
      // — the sole way back to a populated list — so a keyboard user landing on
      // it with no visible change is the worst case, not an edge one.
      '&:focus-visible': {
        outline: `2px solid ${theme.tokens.color.accent}`,
        outlineOffset: '2px',
        borderRadius: `${theme.tokens.radius.badgeSharp}px`,
      },
    })}
  >
    {label}
  </ButtonBase>
);

export interface AdminEmptyHint {
  /** The uppercase key — "Heads up", "Tip", "Reminder", "Example", "Last seen". */
  eyebrow: string;
  body: string;
}

interface AdminEmptyProps {
  /** One of `empty-icons.tsx`, rendered at 56 inside an 88px tile. */
  icon: ReactNode;
  title: string;
  subtitle: string;
  primaryAction: AdminEmptyAction;
  /** The quieter way out — "Browse the catalogue →". Ghost, beside the primary. */
  secondaryAction?: AdminEmptyAction;
  hint?: AdminEmptyHint;
}

export const AdminEmpty = ({
  icon,
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  hint,
}: AdminEmptyProps) => (
  <Box
    sx={{
      maxWidth: '520px',
      mx: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '14px',
      padding: '48px 24px',
      textAlign: 'center',
    }}
  >
    <EmptyAnnouncement>{title}</EmptyAnnouncement>
    <EmptyArt icon={icon} size="lg" />

    <Box
      component="p"
      sx={(theme: Theme) => ({
        margin: 0,
        fontSize: '18px',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: theme.tokens.color.ink,
      })}
    >
      {title}
    </Box>

    <EmptySubtitle>{subtitle}</EmptySubtitle>

    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '10px',
        marginTop: '6px',
      }}
    >
      <Button
        variant="contained"
        startIcon={primaryAction.icon}
        onClick={primaryAction.onClick}
        disabled={primaryAction.disabled}
      >
        {primaryAction.label}
      </Button>
      {secondaryAction ? (
        <Button
          variant="text"
          startIcon={secondaryAction.icon}
          onClick={secondaryAction.onClick}
          disabled={secondaryAction.disabled}
        >
          {secondaryAction.label}
        </Button>
      ) : null}
    </Box>

    {hint ? (
      <Box
        sx={(theme: Theme) => ({
          marginTop: '18px',
          maxWidth: '480px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 14px',
          borderRadius: '10px',
          textAlign: 'left',
          backgroundColor: theme.tokens.color.surfaceMuted,
          border: theme.tokens.border.default,
          fontSize: '12.5px',
          color: theme.tokens.color.ink3,
        })}
      >
        <Box
          component="span"
          sx={(theme: Theme) => ({
            flexShrink: 0,
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: theme.tokens.color.ink4,
          })}
        >
          {hint.eyebrow}
        </Box>
        <Box component="span">{hint.body}</Box>
      </Box>
    ) : null}
  </Box>
);

type AdminEmptyInlineProps = {
  title: string;
  /** The single way back — "Clear filters", "Clear search". */
  action: AdminEmptyLinkAction;
} & (
  | {
      /** Rendered at 36 inside a 56px tile. */
      icon: ReactNode;
      subtitle: string;
      compact?: false;
    }
  | {
      /**
       * `.admin-empty-sm` — the form for a space with no room for art: title
       * and link only, at half the padding. Edit › Prizes' search-empty is the
       * design's one use of it. Art and subtitle are not merely optional here,
       * they are absent — the union is what stops a caller passing an icon and
       * wondering why it never renders.
       */
      compact: true;
      icon?: never;
      subtitle?: never;
    }
);

/**
 * The filtered empty state — `.admin-empty.admin-empty-inline` in
 * `setup-rail.css`, used when filters return zero rows.
 *
 * A separate component rather than a flag on `AdminEmpty`, because the design
 * makes the distinction deliberately: nothing-exists offers creation,
 * filters-matched-nothing offers escape. One component with a boolean would
 * invite a screen to render a "Create" pill over a filtered list.
 */
export const AdminEmptyInline = ({
  icon,
  title,
  subtitle,
  action,
  compact,
}: AdminEmptyInlineProps) => (
  <Box
    sx={(theme: Theme) => ({
      maxWidth: '520px',
      mx: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      ...(compact
        ? // `.admin-empty-sm` — tighter, and no ground: it sits inside a card
          // that already has one.
          { gap: '8px', padding: '24px 16px', backgroundColor: 'transparent' }
        : {
            gap: '14px',
            padding: '48px 24px',
            // The muted ground is what sets this apart from the full-page
            // flavour's box, so it reads as a gap inside a list rather than as
            // the whole screen.
            backgroundColor: theme.tokens.color.surfaceMuted,
          }),
    })}
  >
    <EmptyAnnouncement>{title}</EmptyAnnouncement>
    {compact ? null : <EmptyArt icon={icon} size="sm" />}

    <Box
      component="p"
      sx={(theme: Theme) => ({
        margin: 0,
        // 15px is `.admin-empty-title-sm`, which only the *filtered* flavour
        // wears. `.admin-empty-sm` shortens the box and leaves the type alone,
        // so the compact form keeps the full-page 18px.
        fontSize: compact ? '18px' : '15px',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: theme.tokens.color.ink,
      })}
    >
      {title}
    </Box>

    {subtitle ? <EmptySubtitle>{subtitle}</EmptySubtitle> : null}

    {/* `.admin-empty-actions` carries its 6px in both forms. */}
    <Box sx={{ marginTop: '6px' }}>
      <EmptyTextLink {...action} />
    </Box>
  </Box>
);

/**
 * The treatments the handoff gives a section empty. They are not a matrix:
 * every instance in the bundle pairs a palette with a box size, so one prop
 * drives both.
 *
 * - `accent` is `.rl-empty` — a reel with no outcomes. "Add one."
 * - `warn` is `.m-emptymkts` — a fixture the feed gave no markets for. Smaller,
 *   because it nests one level deeper, inside an expanded fixture row.
 * - `view` is `.vw-empty` — a read-only view section with nothing to report
 *   yet, like the Entries section on a contest that has not opened (CAE-1576).
 *   `accent`'s geometry over the muted ground, and the only one of the three
 *   the handoff draws with no button: nothing here is a gap somebody can fill.
 */
interface SectionTone {
  padding: string;
  gap: string;
  glyphSize: string;
  glyphRadius: string;
  titleSize: string;
  subtitleSize: string;
  radius: string;
  /**
   * Token names rather than values, so the whole treatment stays one row and
   * the colour still comes off the theme (`no-hard-coded-colour.test.ts`).
   */
  glyphBg: keyof ColorTokens;
  glyphInk: keyof ColorTokens;
  /** `.vw-empty` sits on the muted ground; the other two on the surface. */
  background: keyof ColorTokens;
}

const SECTION_TONES = {
  accent: {
    padding: '24px',
    gap: '16px',
    glyphSize: '40px',
    glyphRadius: '10px',
    titleSize: '15px',
    subtitleSize: '13px',
    radius: '12px',
    glyphBg: 'accentSoft',
    glyphInk: 'accent',
    background: 'surface',
  },
  warn: {
    padding: '16px',
    gap: '14px',
    glyphSize: '36px',
    glyphRadius: '10px',
    titleSize: '13.5px',
    subtitleSize: '12.5px',
    radius: '10px',
    glyphBg: 'warnSoft',
    glyphInk: 'warn',
    background: 'surface',
  },
  view: {
    padding: '24px',
    gap: '16px',
    glyphSize: '40px',
    glyphRadius: '10px',
    titleSize: '14px',
    subtitleSize: '12.5px',
    radius: '12px',
    glyphBg: 'accentSoft',
    glyphInk: 'accent',
    background: 'surfaceMuted',
  },
} satisfies Record<string, SectionTone>;

interface AdminEmptySectionProps {
  /** Rendered inside the 36–40px glyph tile, not as full-page art. */
  icon: ReactNode;
  title: string;
  /**
   * A node, not a string — the only one of the three flavours where the design
   * emphasises inside the sentence ("A reel needs at least **two enabled
   * options** before this contest can go live"). The other two are plain text
   * throughout and stay `string`.
   */
  subtitle: ReactNode;
  /**
   * Optional: `.vw-empty` has no button. A section whose emptiness is a fact
   * about the contest's state rather than a gap somebody can fill has nothing
   * to offer, and a button that did nothing would be worse than none.
   */
  action?: AdminEmptyAction;
  tone?: keyof typeof SECTION_TONES;
}

/**
 * The in-section empty state — `.rl-empty` / `.m-emptymkts` in `modals.css`.
 * Used inside an expandable section: a reel with no outcomes, a fixture with no
 * markets.
 *
 * A row rather than a column, and dashed rather than solid: it is a hole in a
 * list that already has content around it, not a screen with nothing on it.
 */
export const AdminEmptySection = ({
  icon,
  title,
  subtitle,
  action,
  tone = 'accent',
}: AdminEmptySectionProps) => {
  const shape = SECTION_TONES[tone];

  return (
    <Box
      sx={(theme: Theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: shape.gap,
        padding: shape.padding,
        borderRadius: shape.radius,
        backgroundColor: theme.tokens.color[shape.background],
        // Dashed, not solid: the design's way of saying "something belongs here
        // and isn't", against the solid borders every filled row carries.
        border: `${theme.tokens.border.width} dashed ${theme.tokens.color.lineStrong}`,
      })}
    >
      <EmptyAnnouncement>{title}</EmptyAnnouncement>
      <Box
        aria-hidden
        sx={(theme: Theme) => ({
          width: shape.glyphSize,
          height: shape.glyphSize,
          flexShrink: 0,
          borderRadius: shape.glyphRadius,
          display: 'grid',
          placeItems: 'center',
          // The handoff draws `MIcon.plus` / `MIcon.refresh` at 13×13 inside
          // these tiles, at both densities.
          fontSize: '13px',
          ...inheritIconSize,
          backgroundColor: theme.tokens.color[shape.glyphBg],
          color: theme.tokens.color[shape.glyphInk],
        })}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          component="p"
          sx={(theme: Theme) => ({
            margin: 0,
            fontSize: shape.titleSize,
            fontWeight: 600,
            letterSpacing: '-0.005em',
            color: theme.tokens.color.ink,
          })}
        >
          {title}
        </Box>
        <Box
          component="p"
          sx={(theme: Theme) => ({
            margin: '3px 0 0',
            fontSize: shape.subtitleSize,
            lineHeight: 1.4,
            color: theme.tokens.color.ink3,
          })}
        >
          {subtitle}
        </Box>
      </Box>

      {action === undefined ? null : (
        <Button
          variant="contained"
          size="small"
          startIcon={action.icon}
          onClick={action.onClick}
          sx={{ flexShrink: 0 }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
};
